import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck } from 'lucide-react';
export function Hero() {
  const smoothScrollTo = (elementId: string) => {
    const target = document.getElementById(elementId);
    if (!target) return;
    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 1200;
    let startTime: number | null = null;
    const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startPosition + distance * easeInOutCubic(progress));
      if (elapsed < duration) requestAnimationFrame(animation);
    };
    requestAnimationFrame(animation);
  };
  return <section className="relative w-full bg-[#0B0E13] text-[#F5F0E8] overflow-hidden pt-12 pb-20 px-4 md:pt-24 md:pb-32" data-id="element-120">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20" data-id="element-121">
        <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] rounded-full bg-[#D4A843] blur-[120px]" data-id="element-122" />
        <div className="absolute top-[40%] -left-[10%] w-[400px] h-[400px] rounded-full bg-[#1A1F2E] blur-[100px]" data-id="element-123" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-124">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="flex flex-col space-y-6" data-id="element-125">
          <div className="inline-flex items-center space-x-2 bg-[#1A1F2E] border border-[#D4A843]/30 rounded-full px-4 py-1.5 w-fit" data-id="element-126">
            <ShieldCheck className="w-4 h-4 text-[#D4A843]" data-id="element-127" />
            <span className="text-xs md:text-sm font-medium text-[#D4A843] tracking-wide uppercase" data-id="element-128">
              Kit Anti-Pérdidas para Compradores
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-[1.1] text-white" data-id="element-129">
            No pierdas tu depósito por un{' '}
            <span className="text-[#D4A843] italic" data-id="element-130">"se me pasó"</span>
          </h1>

          <p className="text-lg md:text-xl text-[#8B8578] leading-relaxed max-w-xl" data-id="element-131">
            El sistema de protección para compradores que quieren control sin
            conflicto. Evita perder miles de dólares en EMD, fees e inspecciones
            por errores de comunicación.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-132">
            <button onClick={() => smoothScrollTo('pricing')} className="group relative px-8 py-4 bg-[#D4A843] hover:bg-[#E8BC5A] text-[#0B0E13] font-bold text-lg rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(212,168,67,0.3)] hover:shadow-[0_0_30px_rgba(212,168,67,0.5)] flex items-center justify-center" data-id="element-133">
              <span data-id="element-134">Obtener el Kit Ahora - $6.36</span>
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-135" />
            </button>
            <p className="text-xs text-[#8B8578] text-center sm:text-left mt-2 sm:mt-0 sm:self-center" data-id="element-136">
              Descarga inmediata • PDF Mobile-first
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="relative mx-auto lg:mx-0" data-id="element-137">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[#D4A843]/20 bg-[#1A1F2E]" data-id="element-138">
            <picture data-id="element-139">
              <img src="https://cdn.magicpatterns.com/uploads/8oZHtwf3HFgjfcG37m714a/40471488-45D4-4C20-BDDA-420E907778FD.jpg" alt="Kit Anti-Pérdidas — Protege tu Depósito (EMD) + Contingencias" className="w-full object-cover max-w-[280px] sm:max-w-[340px] md:max-w-[400px] lg:max-w-md mx-auto" loading="eager" decoding="async" width={800} height={1000} data-id="element-140" />
            </picture>
            {/* Glossy overlay effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-black/40 to-transparent pointer-events-none" data-id="element-141" />
          </div>

          {/* Floating badge */}
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.6
        }} className="absolute -bottom-6 -right-6 md:right-10 bg-[#0F1419] border border-[#D4A843] p-4 rounded-xl shadow-xl max-w-[200px] hidden md:block" data-id="element-142">
            <p className="text-[#D4A843] font-bold text-2xl" data-id="element-143">9 Puntos</p>
            <p className="text-sm text-[#F5F0E8]" data-id="element-144">
              de riesgo identificados para proteger tu dinero
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}